import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { storeService } from '../services/store.service';
import { env } from '../config/env';

export const HomePage: React.FC = () => {
  const { data: categories, isLoading: isCatsLoading } = useQuery({
    queryKey: ['categories'],
    queryFn: () => storeService.getCategories(),
  });

  const { data: featured, isLoading: isFeaturedLoading } = useQuery({
    queryKey: ['featuredProducts'],
    queryFn: () => storeService.getFeaturedProducts(),
  });

  const { data: recommended, isLoading: isRecommendedLoading } = useQuery({
    queryKey: ['recommendedProducts'],
    queryFn: () => storeService.getRecommendedProducts(),
  });

  return (
    <main className="max-w-[1280px] mx-auto px-6 lg:px-10 py-4 md:py-6 space-y-12 md:space-y-20">
      {/* Welcome Banner */}
      <section className="w-full overflow-hidden rounded-[2rem] shadow-2xl relative h-[300px] md:h-[400px] bg-gradient-to-br from-primary to-primary/70">
        <div className="absolute top-0 right-0 w-48 h-48 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-black/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>
        <div className="w-full h-full flex flex-col items-center justify-center px-6 md:px-20 text-center relative z-10">
          <div className="max-w-3xl space-y-6">
            <h1 className="text-white text-4xl md:text-6xl lg:text-7xl font-black leading-[1.1] tracking-tight">
              Welcome to {env.COMPANY_NAME}
            </h1>
            <p className="text-white/80 text-base md:text-lg font-medium leading-relaxed max-w-2xl mx-auto">
              Discover our curated collection of premium products.
            </p>
            <Link
              to="/products"
              className="inline-block bg-white text-primary px-8 py-3 rounded-xl font-black text-sm hover:scale-105 transition-transform shadow-lg"
            >
              Shop Now
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Products Section */}
      <section className="space-y-8">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="text-center md:text-left space-y-1">
            <h2 className="text-2xl lg:text-4xl font-black tracking-tight text-[#111418] dark:text-white">Featured Products</h2>
            <p className="text-gray-500 text-sm">Curated products specifically for you.</p>
          </div>
          <Link to="/products?sort=popularity" className="text-primary text-sm font-bold flex items-center gap-2 hover:gap-4 transition-all">
            Show All <span className="material-symbols-outlined text-base">arrow_forward</span>
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {isFeaturedLoading ? (
            Array(4).fill(0).map((_, i) => (
              <div key={i} className="space-y-4">
                <div className="aspect-square rounded-[1.5rem] bg-gray-100 dark:bg-gray-800 animate-pulse"></div>
                <div className="h-4 w-1/2 bg-gray-100 dark:bg-gray-800 animate-pulse mx-auto"></div>
                <div className="h-4 w-3/4 bg-gray-100 dark:bg-gray-800 animate-pulse mx-auto"></div>
              </div>
            ))
          ) : featured && featured.content.length > 0 ? (
            featured.content.map((prod) => (
              <Link key={prod.productCode} to={`/products/${prod.productCode}`} className="group space-y-3">
                <div className="relative aspect-square rounded-[1.5rem] bg-gray-50 dark:bg-gray-800 overflow-hidden shadow-sm group-hover:shadow-xl transition-all duration-300">
                  <img
                    alt={prod.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    src={prod.thumbnailUrl || prod.images?.[0]?.url || ''}
                  />
                  <button className="absolute top-4 right-4 size-10 flex items-center justify-center rounded-full bg-white/90 dark:bg-black/80 text-[#111418] dark:text-white backdrop-blur-md opacity-0 group-hover:opacity-100 transition-opacity focus:opacity-100 hover:bg-primary hover:text-white shadow-lg">
                    <span className="material-symbols-outlined text-xl">favorite</span>
                  </button>
                  <div className="absolute bottom-4 left-4 right-4 translate-y-full group-hover:translate-y-0 transition-all duration-500 px-2 opacity-0 group-hover:opacity-100">
                    <button className="w-full bg-primary text-white py-3 rounded-xl font-bold text-xs shadow-xl shadow-primary/30 active:scale-95 transition-transform">
                      Quick Add
                    </button>
                  </div>
                </div>
                <div className="flex flex-col items-center text-center space-y-0.5">
                  <p className="text-[9px] font-black uppercase text-primary/60 tracking-[0.2em] font-display">{prod.categoryName}</p>
                  <h3 className="text-base font-bold text-[#111418] dark:text-white hover:text-primary transition-colors cursor-pointer">
                    {prod.name}
                  </h3>
                  <p className="text-lg font-black text-[#111418] dark:text-white">${prod.price.toFixed(2)}</p>
                </div>
              </Link>
            ))
          ) : (
            <p className="col-span-full text-center text-gray-400 py-8">No featured products available.</p>
          )}
        </div>
      </section>

      {/* Recommended Products Section */}
      <section className="space-y-8">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="text-center md:text-left space-y-1">
            <h2 className="text-2xl lg:text-4xl font-black tracking-tight text-[#111418] dark:text-white">Recommended For You</h2>
            <p className="text-gray-500 text-sm">Products we think you'll love.</p>
          </div>
          <Link to="/products?sort=latest" className="text-primary text-sm font-bold flex items-center gap-2 hover:gap-4 transition-all">
            Show All <span className="material-symbols-outlined text-base">arrow_forward</span>
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {isRecommendedLoading ? (
            Array(4).fill(0).map((_, i) => (
              <div key={i} className="space-y-4">
                <div className="aspect-square rounded-[1.5rem] bg-gray-100 dark:bg-gray-800 animate-pulse"></div>
                <div className="h-4 w-1/2 bg-gray-100 dark:bg-gray-800 animate-pulse mx-auto"></div>
                <div className="h-4 w-3/4 bg-gray-100 dark:bg-gray-800 animate-pulse mx-auto"></div>
              </div>
            ))
          ) : recommended && recommended.content.length > 0 ? (
            recommended.content.map((prod) => (
              <Link key={prod.productCode} to={`/products/${prod.productCode}`} className="group space-y-3">
                <div className="relative aspect-square rounded-[1.5rem] bg-gray-50 dark:bg-gray-800 overflow-hidden shadow-sm group-hover:shadow-xl transition-all duration-300">
                  <img
                    alt={prod.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    src={prod.thumbnailUrl || prod.images?.[0]?.url || ''}
                  />
                  <button className="absolute top-4 right-4 size-10 flex items-center justify-center rounded-full bg-white/90 dark:bg-black/80 text-[#111418] dark:text-white backdrop-blur-md opacity-0 group-hover:opacity-100 transition-opacity focus:opacity-100 hover:bg-primary hover:text-white shadow-lg">
                    <span className="material-symbols-outlined text-xl">favorite</span>
                  </button>
                  <div className="absolute bottom-4 left-4 right-4 translate-y-full group-hover:translate-y-0 transition-all duration-500 px-2 opacity-0 group-hover:opacity-100">
                    <button className="w-full bg-primary text-white py-3 rounded-xl font-bold text-xs shadow-xl shadow-primary/30 active:scale-95 transition-transform">
                      Quick Add
                    </button>
                  </div>
                </div>
                <div className="flex flex-col items-center text-center space-y-0.5">
                  <p className="text-[9px] font-black uppercase text-primary/60 tracking-[0.2em] font-display">{prod.categoryName}</p>
                  <h3 className="text-base font-bold text-[#111418] dark:text-white hover:text-primary transition-colors cursor-pointer">
                    {prod.name}
                  </h3>
                  <p className="text-lg font-black text-[#111418] dark:text-white">${prod.price.toFixed(2)}</p>
                </div>
              </Link>
            ))
          ) : (
            <p className="col-span-full text-center text-gray-400 py-8">No recommended products available.</p>
          )}
        </div>
      </section>

      {/* Category Grid */}
      <section className="space-y-8">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="text-center md:text-left space-y-1">
            <h2 className="text-2xl lg:text-4xl font-black tracking-tight text-[#111418] dark:text-white">Shop by Category</h2>
            <p className="text-gray-500 text-sm">Explore our curated collections.</p>
          </div>
          <Link to="/categories" className="text-primary text-sm font-bold flex items-center gap-2 hover:gap-4 transition-all">
            Show All <span className="material-symbols-outlined text-base">arrow_forward</span>
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {isCatsLoading ? (
            Array(4).fill(0).map((_, i) => (
              <div key={i} className="aspect-[4/5] rounded-3xl bg-gray-100 dark:bg-gray-800 animate-pulse"></div>
            ))
          ) : categories && categories.length > 0 ? (
            categories.slice(0, 8).map((cat) => (
              <Link key={cat.id} to={`/products?category=${cat.slug}`} className="group relative aspect-[4/5] rounded-3xl overflow-hidden cursor-pointer shadow-xl">
                {cat.thumbnailUrl ? (
                  <div
                    className="absolute inset-0 bg-cover bg-center group-hover:scale-110 transition-transform duration-700"
                    style={{ backgroundImage: `url("${cat.thumbnailUrl}")` }}
                  ></div>
                ) : (
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/40 to-primary/80 group-hover:scale-110 transition-transform duration-700"></div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
                <div className="absolute bottom-6 left-6 right-6">
                  <h3 className="text-white text-xl font-black">{cat.name}</h3>
                  {cat.description && (
                    <p className="text-white/60 text-xs font-medium tracking-wide mt-1 line-clamp-2">{cat.description}</p>
                  )}
                </div>
              </Link>
            ))
          ) : (
            <p className="col-span-full text-center text-gray-400 py-8">No categories available.</p>
          )}
        </div>
      </section>

    </main>
  );
};
